import {useEffect, useRef} from "react";
import {driver, DriverHook, DriveStep} from "driver.js";
import "driver.js/dist/driver.css";
import {
    closeHamburger,
    closeInteractionBox,
    closeMessageBox,
    DrawerIdMobile,
    getPageUrlForFlowMobile, isHamburgerClosed, isInteractionOpen, isMessageBoxOpen, openHamburger,
    openInteractionBox, openMessageBox,
    tutorialFlowsMobile as tutorialFlows,
    TutorialStepMobile
} from "./tutorialFlowsMobile";
import {useNavigate} from "react-router-dom";
import './index.css';
import "./CommonStaticDialog.css";

export const AppTutorialManagerMobile = () => {
    const navigate = useNavigate();
    const tutorialAnimations = new Set<HTMLElement>();
    const sonarWrappers = new Set<HTMLElement>();
    const currentStepRef = useRef<number>(-1);
    // 🔑 Mutex lock — prevents concurrent handleNextClick / handlePrevClick from racing
    const isNavigatingRef = useRef<boolean>(false);
    const prevEvent = useRef<'Prev' | 'Next'>('Next');

    const getStyleByFlowName = () => {
        const currentState = JSON.parse(localStorage.getItem("tutorialProgressMobile") ?? "{}");
        const flowName = currentState.currentFlow ?? "";
        switch (flowName) {
            case "introFlow":
                return {
                    primaryColor: "#687543",
                    hoverBackgroundColor: "#869064",
                    guardianImage: '/assets/cyborg-bear-1.png',
                };
            case "scamIdentifyFlow":
                return {
                    primaryColor: "#687543",
                    hoverBackgroundColor: "#869064",
                    guardianImage: '/assets/cyborg-bear-1.png',
                };
            case "scamEducationFlow":
                return {
                    primaryColor: "#18475CFF",
                    hoverBackgroundColor: "rgb(24 71 92 / 69%)",

                };
            case "antiScamFlow":
                return {
                    primaryColor: "#553c1a",
                    hoverBackgroundColor: "#664f32f0",
                };
            case "communityFlow":
                return {
                    primaryColor: "#1c2e50",
                    hoverBackgroundColor: "#5581a0",
                };
            case "archivesFlow":
                return {
                    primaryColor: "#832f37",
                    hoverBackgroundColor: "#99444c",
                };
            default:
                return {
                    primaryColor: "#687543",
                    hoverBackgroundColor: "#869064",
                };
        }
    }

    // 🔑 Waits for a drawer to be fully visible in the DOM (not just state-toggled)
    // Polls aria-pressed/MuiModal-hidden, then waits for MuiDrawer-paper transform to settle
    const waitForDrawerVisible = (drawerType: string, timeout = 3000): Promise<boolean> => {
        return new Promise((resolve) => {
            const start = performance.now();

            const check = () => {
                let isOpen = false;
                switch (drawerType) {
                    case DrawerIdMobile.HAMBUGER:
                        isOpen = !isHamburgerClosed();
                        break;
                    case DrawerIdMobile.MESSAGE:
                        isOpen = isMessageBoxOpen();
                        break;
                    case DrawerIdMobile.INTERACTION:
                        isOpen = isInteractionOpen();
                        break;
                }

                if (isOpen) {
                    // Drawer state is open — now wait for the slide transition to finish
                    // by polling until no MuiDrawer-paper has an active CSS transition
                    waitForSlideComplete().then(() => resolve(true));
                    return;
                }

                if (performance.now() - start > timeout) {
                    console.warn("[Mobile Tutorial] waitForDrawerVisible timeout:", drawerType);
                    resolve(false);
                    return;
                }
                requestAnimationFrame(check);
            };
            requestAnimationFrame(check);
        });
    };

    // 🔑 Waits until MUI Drawer slide animation is truly finished
    // by checking that the MuiDrawer-paper transform is identity (no translateX offset)
    const waitForSlideComplete = (timeout = 1500): Promise<void> => {
        return new Promise((resolve) => {
            const start = performance.now();
            const check = () => {
                // Find all visible drawer papers
                const papers = document.querySelectorAll('.MuiDrawer-paper');
                let allSettled = true;
                papers.forEach((paper) => {
                    const style = window.getComputedStyle(paper);
                    const transform = style.transform;
                    // During slide: transform = "matrix(1, 0, 0, 1, -280, 0)" (translateX != 0)
                    // After slide:  transform = "none" or "matrix(1, 0, 0, 1, 0, 0)"
                    if (transform && transform !== 'none') {
                        const match = transform.match(/matrix\(.+,\s*(.+),\s*(.+)\)$/);
                        if (match) {
                            const tx = parseFloat(match[1]);
                            const ty = parseFloat(match[2]);
                            if (Math.abs(tx) > 1 || Math.abs(ty) > 1) {
                                allSettled = false;
                            }
                        }
                    }
                });

                if (allSettled) {
                    // Extra 50ms safety buffer for reflow
                    setTimeout(resolve, 50);
                    return;
                }

                if (performance.now() - start > timeout) {
                    resolve();
                    return;
                }
                requestAnimationFrame(check);
            };
            requestAnimationFrame(check);
        });
    };

    // 🔑 Waits for all drawers to be fully closed
    const waitForDrawersClosed = (timeout = 1500): Promise<void> => {
        return new Promise((resolve) => {
            const start = performance.now();
            const check = () => {
                const allClosed = isHamburgerClosed() && !isMessageBoxOpen() && !isInteractionOpen();
                if (allClosed) {
                    // Wait extra for slide-out animation
                    setTimeout(resolve, 200);
                    return;
                }
                if (performance.now() - start > timeout) {
                    resolve();
                    return;
                }
                requestAnimationFrame(check);
            };
            requestAnimationFrame(check);
        });
    };

    const excute = async (event: () => void): Promise<void> => {
        await new Promise(r => setTimeout(r, 50));
        return event();
    }

    // 🔑 Returns a Promise that resolves only AFTER the required drawer is fully open and visible
    const ensureRequiredDrawerOpen = async (step: any): Promise<void> => {
        if (step?.requiredDrawerOpen && step.requiredDrawerOpen !== '') {
            console.log("[Mobile Tutorial] ensureRequiredDrawerOpen:", step.requiredDrawerOpen);

            // 🔑 FIX: wait one microtask so React can flush state updates from the real click
            // before we check drawer state. Without this, isMessageBoxOpen() may return stale value.
            await new Promise(r => setTimeout(r, 50));

            switch (step.requiredDrawerOpen) {
                case DrawerIdMobile.HAMBUGER:
                    closeMessageBox();
                    closeInteractionBox();
                    openHamburger();
                    break;
                case DrawerIdMobile.MESSAGE:
                    closeHamburger();
                    closeInteractionBox();
                    openMessageBox();
                    break;
                case DrawerIdMobile.INTERACTION:
                    closeHamburger();
                    closeMessageBox();
                    openInteractionBox();
                    break;
            }
            // 🔑 Poll until drawer is actually open + slide animation is done
            await waitForDrawerVisible(step.requiredDrawerOpen);
        } else {
            // Close all drawers and wait for slide-out animation
            closeHamburger();
            closeMessageBox();
            closeInteractionBox();
            await waitForDrawersClosed();
        }
    }

    const showStaticDialog = (options: any) => {
        const {
            title = "Title",
            content = "Content...",
            avatar = "",
            confirmText = "OK",
            cancelText = "Cancel",
            onConfirm = null,
            onCancel = null
        } = options;

        const oldDialog = document.getElementById("common-dialog-backdrop");
        if (oldDialog) oldDialog.remove();

        const wrapper = document.createElement("div");
        const style1 = `
        display: block; border-radius: 12px; font-weight: bold; font-family: Orbitron, sans-serif; box-shadow: rgba(122, 94, 59, 0.4) 0px 0px 6px; font-size: 1.15rem; cursor: pointer; transition: 0.25s; text-shadow: none;
        `
        const avatarSourceImage = `/assets/${avatar}`;
        wrapper.innerHTML = `
                    
                    <div class="common-dialog-backdrop" id="common-dialog-backdrop">
                      <div class="common-dialog">
                        
                        <div class="common-dialog-title">
                         ${avatar ? `<img class="dialog-avatar" src="${avatarSourceImage}" style="width: 64px; height: 64px; border-radius: 12px; box-shadow: rgba(122, 94, 59, 0.5) 0 0 8px; object-fit: contain;" alt=""/>` : ""}
                          <span class="dialog-title-text">${title}</span>
                        </div>
                
                        <div class="common-dialog-content">${content}</div>
                
                        <div class="common-dialog-actions">
                          <button class="dialog-btn-cancel">${cancelText}</button>
                          <button class="dialog-btn-confirm"
                          style="${style1}"
                          >${confirmText}</button>                          
                        </div>
                
                      </div>
                    </div>
                `;

        const dialogEl = wrapper.firstElementChild as HTMLElement;
        if (dialogEl) {
            dialogEl.style.position = "fixed";
            dialogEl.style.zIndex = "2147483647"; // MAX
            (dialogEl as HTMLElement).style.setProperty("z-index", "2147483647", "important");
            document.body.classList.remove("driver-active");
            document.body.classList.remove("driver-fade");
            document.body.appendChild(dialogEl);
        }

        // Actions
        const backdrop = document.getElementById("common-dialog-backdrop");
        if (backdrop) {
            const backdrop = document.getElementById("common-dialog-backdrop") as HTMLElement;
            backdrop.style.position = "fixed";
            backdrop.style.top = "0";
            backdrop.style.left = "0";
            backdrop.style.setProperty("z-index", "2147483647", "important");
            const styleObject = getStyleByFlowName();

            const btnCancel = backdrop.querySelector(".dialog-btn-cancel") as HTMLButtonElement | null;
            if (btnCancel) {
                Object.assign(btnCancel!.style, {
                    borderRadius: "12px",
                    fontWeight: "bold",
                    fontFamily: '"Orbitron", sans-serif',
                    backgroundColor: "rgba(255,255,255,0.6)",
                    color: styleObject.primaryColor,
                    boxShadow: "0 0 6px rgba(122, 94, 59, 0.4)",
                    fontSize: {lg: '1.15rem', md: "0.875rem"},
                    cursor: "pointer",
                    transition: "all 0.25s ease",
                    textShadow: "none",
                    position: "relative",
                });
                btnCancel.addEventListener("mouseenter", () => {
                    btnCancel.style.backgroundColor = styleObject.hoverBackgroundColor;
                    btnCancel.style.color = "white";
                    btnCancel.style.boxShadow = "0 0 12px #7a5e3b";
                });
                btnCancel.addEventListener("mouseleave", () => {
                    btnCancel.style.backgroundColor = "rgba(255,255,255,0.6)";
                    btnCancel.style.color = styleObject.primaryColor;
                    btnCancel.style.boxShadow = "0 0 6px rgba(122, 94, 59, 0.4)";
                });
            }
            const btnConfirm = backdrop.querySelector(".dialog-btn-confirm") as HTMLButtonElement | null;
            if (btnConfirm) {
                Object.assign(btnConfirm!.style, {
                    borderRadius: "12px",
                    fontWeight: "bold",
                    fontFamily: '"Orbitron", sans-serif',
                    backgroundColor: "rgba(255,255,255,0.6)",
                    color: styleObject.primaryColor,
                    boxShadow: "0 0 6px rgba(122, 94, 59, 0.4)",
                    fontSize: {lg: '1.15rem', md: "0.875rem"},
                    cursor: "pointer",
                    transition: "all 0.25s ease",
                    textShadow: "none",
                    position: "relative",
                });
                btnConfirm.addEventListener("mouseenter", () => {
                    btnConfirm.style.backgroundColor = styleObject.hoverBackgroundColor;
                    btnConfirm.style.color = "white";
                    btnConfirm.style.boxShadow = "0 0 12px #7a5e3b";
                });
                btnConfirm.addEventListener("mouseleave", () => {
                    btnConfirm.style.backgroundColor = "rgba(255,255,255,0.6)";
                    btnConfirm.style.color = styleObject.primaryColor;
                    btnConfirm.style.boxShadow = "0 0 6px rgba(122, 94, 59, 0.4)";
                });
            }


            if (btnCancel)
                btnCancel.onclick = () => {
                    document.body.classList.add("driver-fade");
                    document.body.classList.add("driver-active");
                    backdrop.remove();
                    onCancel && onCancel();
                };

            if (btnConfirm)
                btnConfirm.onclick = () => {
                    backdrop.remove();
                    onConfirm && onConfirm();
                };
        }
    }

    useEffect(() => {
        const hasDone = localStorage.getItem("hasCompletedTutorialMobile");
        if (hasDone) return;

        const flowChain = [
            "introFlow",
            "scamIdentifyFlow",
            "scamEducationFlow",
            "antiScamFlow",
            "communityFlow",
            "archivesFlow",
        ];

        const getNextFlow = (flowName: string) => {
            const idx = flowChain.indexOf(flowName);
            return idx >= 0 && idx < flowChain.length - 1 ? flowChain[idx + 1] : null;
        };

        const getPrevFlow = (flowName: string) => {
            const idx = flowChain.indexOf(flowName);
            return idx > 0 ? flowChain[idx - 1] : null;
        };

        const handleNextClick = async (driverInstance: any, flowName: string, steps: any[], waitForElement: any) => {
            if (isNavigatingRef.current) {
                return;
            }
            isNavigatingRef.current = true;
            prevEvent.current = 'Next';

            try {
                const state = driverInstance.getState();
                const isLastStep = state.activeIndex === steps.length - 1;

                if (isLastStep) {
                    const nextFlow = getNextFlow(flowName);
                    if (nextFlow) {
                        localStorage.setItem("tutorialProgressMobile", JSON.stringify({
                            currentFlow: nextFlow,
                            currentStep: 0
                        }));
                        currentStepRef.current = -1;
                        driverInstance.destroy();
                        navigate(getPageUrlForFlowMobile(nextFlow));
                        setTimeout(() => startFlow(nextFlow, 0), 300);
                    } else {
                        localStorage.setItem("hasCompletedTutorialMobile", "true");
                        currentStepRef.current = -1;
                        driverInstance.destroy();
                    }
                    return;
                }

                // ⭐ Xử lý bước bình thường
                const nextIndex = state.activeIndex + 1;
                const nextStep = steps[nextIndex];
                console.log("[Mobile Tutorial] handleNextClick → nextStep", nextIndex, nextStep);

                if (nextStep?.selector) {
                    const waitTimeout = nextStep.requiredDrawerOpen ? 3000 : 1500;
                    waitForElement(nextStep.selector, waitTimeout).then((el: any) => {
                        if (!el) {
                            console.warn("[Mobile Tutorial] element not found:", nextStep.selector);
                        }
                    })
                }

                if (nextIndex !== currentStepRef.current) {
                    const currentStep = steps[state.activeIndex];
                    if (currentStep?.preExecuteWhenNext) {
                        excute(currentStep?.preExecuteWhenNext).then(() => {
                            currentStepRef.current = nextIndex;
                            driverInstance.moveNext();
                        });
                    } else {
                        currentStepRef.current = nextIndex;
                        driverInstance.moveNext();
                    }


                    console.log("[Mobile Tutorial] currentStepRef →", currentStepRef.current);
                }
            } finally {
                // 🔑 Release lock after a short delay so driver.js finishes its internal cycle
                setTimeout(() => {
                    isNavigatingRef.current = false;
                }, 100);
            }
        };

        const savedProgress = JSON.parse(localStorage.getItem("tutorialProgressMobile") || "{}");
        const currentFlow = savedProgress.currentFlow || flowChain[0];
        const currentStep = savedProgress.currentStep || 0;

        const startFlow = (flowName: string, startIndex = 0) => {
            const steps = tutorialFlows[flowName];

            if (!steps) return;

            const driverInstance = driver({
                animate: true,
                overlayOpacity: 0.6,
                overlayColor: "rgba(0,0,0,0.75)",
                allowClose: false,
                disableActiveInteraction: false,
                stagePadding: 8,
                stageRadius: 10,
                showProgress: false,
                smoothScroll: true,
                progressText: "{{current}} / {20}",
                showButtons: ["next", "previous"],

                onDestroyed: () => {
                    const skipBtn = document.querySelector(".tutorial-skip-btn-mobile");
                    if (skipBtn) skipBtn.remove();
                },
                onHighlightStarted: (_el, _step, opts) => {
                    const state = driverInstance.getState();
                    const isLastStep = state.activeIndex === steps.length - 1;
                    if (prevEvent.current === 'Prev') {
                        let prevStepN = null;
                        if (isLastStep) {
                            const nextFlow = getNextFlow(flowName);
                            prevStepN = nextFlow ? tutorialFlows[nextFlow][0] : null;
                        } else {
                            prevStepN = steps[state.activeIndex + 1];
                        }
                        if (prevStepN?.preExecuteWhenPrev) {
                            prevStepN?.preExecuteWhenPrev()
                        }
                    } else if (prevEvent.current === 'Next') {
                        // const prevStepN = steps[state.activeIndex - 1];
                        // if (prevStepN?.preExecuteWhenNext) {
                        //     // excute(prevStepN?.preExecuteWhenNext);
                        //     prevStepN?.preExecuteWhenNext()
                        // }
                        if (state.activeIndex === 0) {
                            console.log('closeHamburger')
                            closeHamburger();
                        }
                    }
                    // const currentStepN = steps[state.activeIndex];
                    // if (currentStepN?.preExecuteWhenNext) {
                    //     excute(currentStepN?.preExecuteWhenNext);
                    // }

                },

                onPopoverRender: (popover) => {
                    const state = driverInstance.getState();
                    // const currentStepN = steps[state.activeIndex];
                    // if (currentStepN?.preExecuteWhenNext) {
                    //     excute(currentStepN?.preExecuteWhenNext);
                    // }
                    sonarWrappers.forEach(sonar => sonar.remove());
                    sonarWrappers.clear();
                    document.querySelectorAll(".sonar-wrapper").forEach(el => el.remove());

                    Object.assign(popover.wrapper.style, {
                        background: "rgba(255,255,255,0.9)",
                        border: "2px solid #7a5e3b",
                        borderRadius: "12px",
                        fontFamily: '"Orbitron", sans-serif',
                        color: "#414335",
                        padding: "16px",
                        maxWidth: "560px",
                        visibility: steps[state.activeIndex].hidePopover ? "hidden" : "visible",
                    });

                    const styleObject = getStyleByFlowName();
                    const title = popover.wrapper.querySelector("#driver-popover-title") as HTMLElement;
                    if (title) {
                        Object.assign(title.style, {
                            fontWeight: "bold",
                            textTransform: "uppercase",
                            color: styleObject.primaryColor,
                            letterSpacing: "1px",
                            fontSize: {lg: '1.4rem', md: "0.875rem"},
                        });
                    }

                    const content = popover.wrapper.querySelector("#cyborg-popover-content") as HTMLElement;
                    if (content) {
                        Object.assign(content.style, {
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "10px",
                        });
                    }

                    const imageBox = popover.wrapper.querySelector("#cyborg-image-box img") as HTMLImageElement;
                    if (imageBox) {
                        Object.assign(imageBox.style, {
                            width: "64px",
                            height: "64px",
                            borderRadius: "12px",
                            boxShadow: "0 0 8px rgba(122, 94, 59, 0.5)",
                            objectFit: "contain",
                        });
                    }

                    const textBox = popover.wrapper.querySelector("#cyborg-text-box") as HTMLElement;
                    if (textBox) {
                        Object.assign(textBox.style, {
                            lineHeight: 1.43,
                            color: "#414335",
                            fontWeight: 700,
                            fontSize: '1.125rem',
                        });
                    }

                    const progressTextBox = popover.wrapper.querySelector(".driver-popover-progress-text") as HTMLElement;
                    if (progressTextBox) {
                        Object.assign(progressTextBox.style, {
                            lineHeight: 1.43,
                            color: "#414335",
                            fontWeight: 700,
                            fontSize: '1.125rem',
                        });
                    }


                    const nextBtn = popover.wrapper.querySelector(".driver-popover-next-btn") as HTMLElement;
                    const isMustClick = popover.wrapper.querySelector("#is-must-click") as HTMLElement;
                    const isMustClickWithNoAction = popover.wrapper.querySelector("#isMustClickWithNoAction") as HTMLElement;
                    const isEndStepElement = popover.wrapper.querySelector("#is-end-step") as HTMLElement;
                    const isEndStep = isEndStepElement.innerText.toLowerCase() === 'true';
                    const isMustClickBool = isMustClick && isMustClick.innerText === 'is-must-click';
                    const isMustClickWithNoActionBool = isMustClickWithNoAction && isMustClickWithNoAction.innerText === 'isMustClickWithNoAction';
                    if (nextBtn) {
                        Object.assign(nextBtn.style, {
                            borderRadius: "12px",
                            fontWeight: "bold",
                            fontFamily: '"Orbitron", sans-serif',
                            backgroundColor: "rgba(255,255,255,0.6)",
                            color: styleObject.primaryColor,
                            boxShadow: "0 0 6px rgba(122, 94, 59, 0.4)",
                            fontSize: {lg: '1.15rem', md: "0.875rem"},
                            cursor: "pointer",
                            transition: "all 0.25s ease",
                            display: isMustClickBool || isMustClickWithNoActionBool ? "none" : "block",
                            textShadow: "none",
                            position: "relative",
                        });
                        nextBtn.addEventListener("mouseenter", () => {
                            nextBtn.style.backgroundColor = styleObject.hoverBackgroundColor;
                            nextBtn.style.color = "white";
                            nextBtn.style.boxShadow = "0 0 12px #7a5e3b";
                        });
                        nextBtn.addEventListener("mouseleave", () => {
                            nextBtn.style.backgroundColor = "rgba(255,255,255,0.6)";
                            nextBtn.style.color = styleObject.primaryColor;
                            nextBtn.style.boxShadow = "0 0 6px rgba(122, 94, 59, 0.4)";
                        });
                        if (isEndStep) {
                            nextBtn.innerText = 'End of Introduction';
                            currentStepRef.current = -1;
                        }
                        const sonarWrapper = document.createElement("div");
                        sonarWrapper.className = "sonar-wrapper";
                        sonarWrapper.innerHTML = `
                                  <div class="sonar-emitter">
                                    <div class="sonar-wave sonar-wave1"></div>
                                    <div class="sonar-wave sonar-wave2"></div>
                                    <div class="sonar-wave sonar-wave3"></div>
                                    <div class="sonar-wave sonar-wave4"></div>
                                  </div>
                                `;

                        Object.assign(sonarWrapper.style, {
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "30px",
                            height: "30px",
                            pointerEvents: "none",
                            zIndex: "0",
                        });
                        nextBtn.appendChild(sonarWrapper);
                        sonarWrappers.add(sonarWrapper);
                    }

                    const prevBtn = popover.wrapper.querySelector(".driver-popover-prev-btn") as any;
                    if (prevBtn) {
                        Object.assign(prevBtn.style, {
                            borderRadius: "12px",
                            fontWeight: "bold",
                            fontFamily: '"Orbitron", sans-serif',
                            backgroundColor: "rgba(255,255,255,0.6)",
                            color: styleObject.primaryColor,
                            boxShadow: "0 0 6px rgba(122, 94, 59, 0.4)",
                            fontSize: {lg: '1.15rem', md: "0.875rem"},
                            cursor: "pointer",
                            transition: "all 0.25s ease",
                            textShadow: "none",
                        });
                        prevBtn.removeAttribute("disabled");
                        prevBtn.classList.remove("driver-popover-btn-disabled");
                        prevBtn.addEventListener("mouseenter", () => {
                            prevBtn.style.backgroundColor = styleObject.hoverBackgroundColor;
                            prevBtn.style.color = "white";
                            prevBtn.style.boxShadow = "0 0 12px #7a5e3b";
                        });
                        prevBtn.addEventListener("mouseleave", () => {
                            prevBtn.style.backgroundColor = "rgba(255,255,255,0.6)";
                            prevBtn.style.color = styleObject.primaryColor;
                            prevBtn.style.boxShadow = "0 0 6px rgba(122, 94, 59, 0.4)";
                        });
                    }

                    localStorage.setItem(
                        "tutorialProgressMobile",
                        JSON.stringify({currentFlow: flowName, currentStep: state.activeIndex})
                    );
                },

                steps: steps.map((step, index) => {

                    // const currentState = JSON.parse(localStorage.getItem("tutorialProgressMobile") ?? "{}");
                    // const flowName = currentState.currentFlow ?? "";
                    //
                    // const state = driverInstance.getState();
                    // const isLastStepOfFlow = state.activeIndex === steps.length - 1;
                    const isEndStep = flowName === 'archivesFlow' && index === steps.length - 1;

                    const handleNextClickInternal = async () => handleNextClick(driverInstance, flowName, steps, waitForElement);

                    const handlePrevClick: DriverHook = (_element, _step) => {
                        // 🔑 Mutex — if another navigation is in-flight, bail out
                        if (isNavigatingRef.current) {
                            console.log("[Mobile Tutorial] handlePrevClick skipped — already navigating");
                            return;
                        }
                        isNavigatingRef.current = true;
                        prevEvent.current = 'Prev';

                        const state = driverInstance.getState();
                        const isFirstStep = state.activeIndex === 0;

                        if (isFirstStep) {
                            const prevFlow = getPrevFlow(flowName);
                            if (prevFlow) {
                                localStorage.setItem(
                                    "tutorialProgressMobile",
                                    JSON.stringify({
                                        currentFlow: prevFlow,
                                        currentStep: tutorialFlows[prevFlow].length - 1,
                                    })
                                );

                                driverInstance.destroy();
                                navigate(getPageUrlForFlowMobile(prevFlow));
                                setTimeout(() => {
                                    // startFlow(prevFlow, tutorialFlows[prevFlow].length - 1);
                                    isNavigatingRef.current = false;
                                }, 100);
                                return;
                            }
                        }

                        // Nếu là introFlow step 0 → không làm gì
                        if (!getPrevFlow(flowName) && state.activeIndex === 0) {
                            isNavigatingRef.current = false;
                            return;
                        }

                        const prevIndex = state.activeIndex - 1;
                        // 🔑 Use .then() (NOT async/await) because DriverHook does NOT await Promises.
                        // This ensures ensureRequiredDrawerOpen finishes before movePrevious() runs.
                        // ensureRequiredDrawerOpen(steps[prevIndex]).then(() => {
                        //
                        // });
                        // if (steps[prevIndex]?.preExecuteWhenPrev) {
                        //     excute(steps[prevIndex]?.preExecuteWhenPrev!).then(() => {
                        //         currentStepRef.current = prevIndex;
                        //         driverInstance.movePrevious();
                        //         sonarWrappers.forEach(sonar => sonar.remove());
                        //         sonarWrappers.clear();
                        //         setTimeout(() => {
                        //             isNavigatingRef.current = false;
                        //         }, 100);
                        //     });
                        // } else {
                        currentStepRef.current = prevIndex;
                        driverInstance.movePrevious();
                        sonarWrappers.forEach(sonar => sonar.remove());
                        sonarWrappers.clear();
                        setTimeout(() => {
                            isNavigatingRef.current = false;
                        }, 100);
                        // }
                    };

                    const handleHighlighted: DriverHook = (_el, _step, opts) => {
                        document.querySelectorAll("[data-disabled-by-tutorial]").forEach(btn => {
                            (btn as HTMLElement).style.pointerEvents = "";
                            btn.removeAttribute("data-disabled-by-tutorial");
                        });
                        const {driver} = opts;

                        // const currentStepN = steps[state.activeIndex];
                        // if (step?.preExecuteWhenNext) {
                        //     excute(step?.preExecuteWhenNext);
                        // }

                        if (step.isMustClick) {
                            const btn = document.querySelector(step.selector!) as HTMLElement;
                            if (btn) {
                                const nextBtn = (driver.getState("popover") as any)?.nextButton;
                                if (nextBtn) nextBtn.disabled = true;

                                btn.querySelectorAll(".sonar-wrapper").forEach(el => el.remove());

                                const sonarWrapper = document.createElement("div");
                                sonarWrapper.className = "sonar-wrapper";
                                sonarWrapper.innerHTML = `
                                  <div class="sonar-emitter">
                                    <div class="sonar-wave sonar-wave1"></div>
                                    <div class="sonar-wave sonar-wave2"></div>
                                    <div class="sonar-wave sonar-wave3"></div>
                                    <div class="sonar-wave sonar-wave4"></div>
                                  </div>
                                `;

                                Object.assign(sonarWrapper.style, {
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    width: "30px",
                                    height: "30px",
                                    pointerEvents: "none",
                                    zIndex: "0",
                                });
                                btn.appendChild(sonarWrapper);
                                sonarWrappers.add(sonarWrapper);
                                tutorialAnimations.add(btn);

                                // 🔑 isMustClick: bubble phase so React handler fires FIRST
                                // (opens drawer / does real action). We then wait a tick for React
                                // to flush its state update before calling handleNextClick, so
                                // ensureRequiredDrawerOpen reads the correct aria-pressed value
                                // and doesn't accidentally double-toggle the drawer.
                                const onMustClick = (e: Event) => {
                                    e.stopImmediatePropagation();
                                    btn.removeEventListener("click", onMustClick, false);
                                    // 🔑 Wait for React to flush state before proceeding
                                    setTimeout(() => {
                                        handleNextClick(driverInstance, flowName, steps, waitForElement).then(() => {
                                            sonarWrapper.remove();
                                        });
                                    }, 80);
                                };
                                btn.addEventListener("click", onMustClick, false);
                            }
                        } else if (step.isMustClickWithNoAction) {
                            const btn = document.querySelector(step.selector!) as HTMLElement;
                            if (btn) {
                                const nextBtn = (driver.getState("popover") as any)?.nextButton;
                                if (nextBtn) nextBtn.disabled = true;
                                btn.querySelectorAll(".sonar-wrapper").forEach(el => el.remove());

                                const sonarWrapper = document.createElement("div");
                                sonarWrapper.className = "sonar-wrapper";
                                sonarWrapper.innerHTML = `
                                      <div class="sonar-emitter">
                                        <div class="sonar-wave sonar-wave1"></div>
                                        <div class="sonar-wave sonar-wave2"></div>
                                        <div class="sonar-wave sonar-wave3"></div>
                                        <div class="sonar-wave sonar-wave4"></div>
                                      </div>
                                    `;

                                Object.assign(sonarWrapper.style, {
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    width: "30px",
                                    height: "30px",
                                    pointerEvents: "none",
                                    zIndex: "0",
                                });
                                btn.appendChild(sonarWrapper);
                                sonarWrappers.add(sonarWrapper);
                                tutorialAnimations.add(btn);

                                // 🔑 capture phase + full stop — isMustClickWithNoAction should NOT trigger real action
                                const onMustClickNoAction = (e: Event) => {
                                    e.stopImmediatePropagation();
                                    e.stopPropagation();
                                    e.preventDefault();
                                    btn.removeEventListener("click", onMustClickNoAction, true);
                                    handleNextClick(driverInstance, flowName, steps, waitForElement).finally(() => {
                                        sonarWrapper.remove();
                                    });
                                };
                                btn.addEventListener("click", onMustClickNoAction, true);
                            }
                        }

                        if (_el) {
                            // 🔑 FIX: When step is isMustClick/isMustClickWithNoAction, the target
                            // button MUST remain clickable. Only disable OTHER inner interactive elements.
                            const mustClickTarget = (step.isMustClick || step.isMustClickWithNoAction)
                                ? document.querySelector(step.selector!) as HTMLElement | null
                                : null;

                            const innerButtons = _el.querySelectorAll(
                                "button, a, [role='button'], span.MuiTouchRipple-root, button div.MuiBox-root, button div.MuiBox-root svg, button div.MuiBox-root svg path, div.MuiButtonBase-root,div.MuiListItemButton-root, div.MuiListItemIcon-root,div.MuiSvgIcon-root, svg.muiButtonIcon"
                            ) as NodeListOf<HTMLElement>;

                            innerButtons.forEach((btn) => {
                                // 🔑 Skip the must-click target and its descendants — they need to stay clickable
                                if (mustClickTarget && (btn === mustClickTarget || mustClickTarget.contains(btn) || btn.contains(mustClickTarget))) {
                                    return;
                                }
                                btn.setAttribute("data-disabled-by-tutorial", "true");
                                btn.style.pointerEvents = "none";
                                btn.style.cursor = "default";
                            });

                            const unhighlight = () => {
                                console.log('unhighlight')
                                innerButtons.forEach((btn) => {
                                    if (btn.getAttribute("data-disabled-by-tutorial")) {
                                        btn.style.pointerEvents = "auto";
                                        btn.style.cursor = "pointer";
                                        btn.removeAttribute("data-disabled-by-tutorial");
                                    }
                                });
                                sonarWrappers.forEach(sonar => sonar.remove());
                                sonarWrappers.clear();
                                document.querySelectorAll(".sonar-wrapper").forEach(el => el.remove());
                                _el.removeEventListener("driver:unhighlight", unhighlight);
                            };
                            _el.addEventListener("driver:unhighlight", unhighlight);
                        }

                        _el?.addEventListener("driver:unhighlight", () => {
                            document.querySelectorAll(".tutorial-sonar-global").forEach(el => el.remove());
                            sonarWrappers.forEach(sonar => sonar.remove());
                            sonarWrappers.clear();
                            document.querySelectorAll(".sonar-wrapper").forEach(el => el.remove());
                        });
                    };

                    return {
                        ...step,
                        element: step.selector,
                        popover: {
                            title: `${step.title}`,
                            description: buildDescription(step, isEndStep),
                            side: "bottom",
                            align: "center",

                            onNextClick: handleNextClickInternal,
                            onPrevClick: handlePrevClick,
                        },
                        onHighlighted: handleHighlighted,
                    } as DriveStep;
                }),
            });

            // async function waitForElement(selector: string, timeout = 1000): Promise<boolean> {
            //     return new Promise((resolve) => {
            //         const start = Date.now();
            //         const timer = setInterval(() => {
            //             if (document.querySelector(selector)) {
            //                 clearInterval(timer);
            //                 resolve(true);
            //             } else if (Date.now() - start > timeout) {
            //                 clearInterval(timer);
            //                 resolve(false);
            //             }
            //         }, 100);
            //     });
            // }
            function waitForElement(selector: string, timeout = 1500): Promise<Element | null> {
                return new Promise(resolve => {
                    let frameId: number;
                    const start = performance.now();

                    const isElementVisible = (el: Element): boolean => {
                        const rect = el.getBoundingClientRect();
                        // Element must have non-zero size and be at least partially in the viewport
                        return rect.width > 0 && rect.height > 0
                            && rect.bottom > 0 && rect.top < window.innerHeight
                            && rect.right > 0 && rect.left < window.innerWidth;
                    };

                    const check = () => {
                        const el = document.querySelector(selector);

                        if (el && isElementVisible(el)) {
                            cancelAnimationFrame(frameId);
                            resolve(el);
                            return;
                        }

                        if (performance.now() - start > timeout) {
                            cancelAnimationFrame(frameId);
                            // Still resolve with the element if found but not visible (fallback)
                            resolve(el || null);
                            return;
                        }

                        frameId = requestAnimationFrame(check);
                    };

                    frameId = requestAnimationFrame(check);
                });
            }


            function buildDescription(step: TutorialStepMobile, isEndStep?: boolean) {
                const srcImg = `src=${process.env.PUBLIC_URL}/assets/${step.image}`;
                const isMustClick = step.isMustClick ? "is-must-click" : "";
                const isMustClickWithNoAction = step.isMustClickWithNoAction ? "isMustClickWithNoAction" : "";

                return `
                    <div id="cyborg-popover-content">
                        <div id="is-must-click" style="display:none">${isMustClick}</div>
                        <div id="elementMustClickBeforeNext" style="display:none">${step.elementMustClickBeforeNext ?? ''}</div>
                        <div id="is-end-step" style="display:none">${isEndStep ? 'true' : 'false'}</div>
                        <div id="isMustClickWithNoAction" style="display:none">${isMustClickWithNoAction}</div>
                        <div id="cyborg-image-box">
                            <img ${srcImg} alt="Cyborg Bear" />
                        </div>
                        <div id="cyborg-text-box">
                            <p>${step.message}</p>
                        </div>
                    </div>`;
            }

            const handleConfirmSkip = () => {
                // Cleanup animations
                tutorialAnimations.forEach(btn => {
                    btn.getAnimations().forEach(animation => animation.cancel());
                    btn.style.boxShadow = "none";
                });
                tutorialAnimations.clear();

                // Re-enable all disabled elements
                document.querySelectorAll("[data-disabled-by-tutorial]").forEach(btn => {
                    (btn as HTMLElement).style.pointerEvents = "";
                    btn.removeAttribute("data-disabled-by-tutorial");
                    btn.getAnimations().forEach((anim: any) => anim.cancel());
                });

                sonarWrappers.forEach(sonar => sonar.remove());
                sonarWrappers.clear();
                document.querySelectorAll(".sonar-wrapper").forEach(el => el.remove());

                localStorage.setItem("hasCompletedTutorialMobile", "true");

                // Destroy existing driver instance safely
                currentStepRef.current = -1;
                const skipBtn = document.querySelector(".tutorial-skip-btn-mobile");
                skipBtn?.remove();
                driverInstance?.destroy?.();
            };

            // 🚀 Start flow
            setTimeout(async () => {
                const currentStepElement = steps[startIndex];
                if (currentStepElement) {
                    // await ensureRequiredDrawerOpen(currentStepElement);
                    const firstSelector = currentStepElement.selector;
                    if (firstSelector) {
                        const waitTimeout = currentStepElement.requiredDrawerOpen ? 3000 : 1500;
                        await waitForElement(firstSelector, waitTimeout);
                    }
                }

                (driverInstance as any).drive(startIndex, {
                    allowPreviousOnFirstStep: true
                });
            }, 100);

            const styleObject = getStyleByFlowName();

            // 🟢 Skip Button (mobile-specific class to avoid conflicting with desktop .tutorial-skip-btn)
            let skipBtn = document.querySelector(".tutorial-skip-btn-mobile") as HTMLButtonElement;
            if (!skipBtn) {
                skipBtn = document.createElement("button");
                skipBtn.className = "tutorial-skip-btn-mobile";
                skipBtn.innerText = "Skip Introduction ✖";
                Object.assign(skipBtn.style, {
                    position: "fixed",
                    top: "60px",
                    right: "10px",
                    zIndex: "99999999",
                    borderRadius: "8px",
                    fontWeight: "bold",
                    fontFamily: '"Orbitron", sans-serif',
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    color: styleObject.primaryColor,
                    fontSize: "0.8rem",
                    cursor: "pointer",
                    pointerEvents: "auto",
                    border: 'transparent',
                    padding: '6px 8px',
                });
                skipBtn.addEventListener("mouseenter", () => {
                    skipBtn.style.backgroundColor = styleObject.hoverBackgroundColor;
                    skipBtn.style.color = "white";
                    skipBtn.style.boxShadow = "0 0 12px #7a5e3b";
                });
                skipBtn.addEventListener("mouseleave", () => {
                    skipBtn.style.backgroundColor = "rgba(255,255,255,0.6)";
                    skipBtn.style.color = styleObject.primaryColor;
                    skipBtn.style.boxShadow = "0 0 6px rgba(122, 94, 59, 0.4)";
                });


                skipBtn.onclick = () => {
                    const currentState = JSON.parse(localStorage.getItem('tutorialProgressMobile') as string);
                    if (currentState && currentState.currentStep === 2 && currentState.currentFlow === 'introFlow') {

                    } else {
                        const currentStep1 = tutorialFlows[currentState.currentFlow][currentState.currentStep];
                        showStaticDialog({
                            title: "Skip Introduction",
                            content: "Do you really want to skip the introduction?",
                            avatar: currentStep1.image,
                            confirmText: "Yes, skip",
                            cancelText: "Cancel",
                            onConfirm: () => {
                                handleConfirmSkip();
                                sonarWrappers.forEach(sonar => sonar.remove());
                                sonarWrappers.clear();
                            },
                        });
                    }
                }

                document.body.appendChild(skipBtn);
            }
        };

        startFlow(currentFlow, currentStep);
    }, [navigate]);

    return null;
};
